const STORAGE_KEY = 'hvac048_user_location';

const US_STATES = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia'
};

class Hvac048LocalizedMessaging {
  constructor() {
    this.modal = document.getElementById('hvac-048-location-modal');
    if (!this.modal) return;

    if (this.modal.parentElement !== document.body) {
      document.body.appendChild(this.modal);
    }

    this.triggers = document.querySelectorAll('[data-hvac-048-location-trigger]');
    this.cityStateTargets = document.querySelectorAll('[data-hvac-048-city-state]');
    this.stateTargets = document.querySelectorAll('[data-hvac-048-state]');
    this.closeButtons = this.modal.querySelectorAll('[data-hvac-048-modal-close]');
    this.form = this.modal.querySelector('[data-hvac-048-location-form]');
    this.zipInput = this.modal.querySelector('[data-hvac-048-zip-input]');
    this.errorEl = this.modal.querySelector('[data-hvac-048-location-error]');
    this.submitButton = this.form ? this.form.querySelector('[type="submit"]') : null;
    this.activeTrigger = null;
    this.location = null;
    this.desktopQuery = window.matchMedia('(min-width: 750px)');

    this.init();
  }

  init() {
    const stored = this.getStoredLocation();
    if (stored) {
      this.applyLocation(stored);
    } else {
      this.detectFromIp();
    }

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.openModal(trigger);
      });
    });

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', () => this.closeModal());
    });

    this.modal.querySelector('.hvac-048-location-modal__overlay')?.addEventListener('click', () => {
      this.closeModal();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.modal.classList.contains('is-open')) {
        this.closeModal();
      }
    });

    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitZip();
      });
    }

    window.addEventListener('resize', () => {
      if (this.modal.classList.contains('is-open') && this.activeTrigger) {
        this.positionPanel(this.activeTrigger);
      }
    });
  }

  getStoredLocation() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  storeLocation(location) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
    } catch (error) {
      return;
    }
    this.location = location;
  }

  normalizeStateCode(code) {
    if (!code || typeof code !== 'string') return '';
    return code.trim().toUpperCase();
  }

  resolveStateName(stateCode, stateName) {
    const code = this.normalizeStateCode(stateCode);
    if (code && US_STATES[code]) return US_STATES[code];
    if (stateName && typeof stateName === 'string') return stateName.trim();
    return code || '';
  }

  formatCityState(location) {
    const code = this.normalizeStateCode(location.stateCode);
    const city = location.city ? location.city.trim() : '';

    if (city && code) return `${city}, ${code}`;
    if (code) return code;
    if (location.state) return location.state;
    return '';
  }

  applyLocation(location) {
    this.location = location;
    const cityState = this.formatCityState(location);
    const stateName = this.resolveStateName(location.stateCode, location.state);

    this.cityStateTargets.forEach((target) => {
      target.textContent = cityState;
    });

    this.stateTargets.forEach((target) => {
      target.textContent = stateName;
    });
  }

  storeAndApply(location) {
    this.storeLocation(location);
    this.applyLocation(location);
  }

  async detectFromIp() {
    const providers = [
      () => fetch('https://ipapi.co/json/').then((response) => {
        if (!response.ok) throw new Error('ipapi failed');
        return response.json();
      }).then((data) => ({
        city: data.city || '',
        stateCode: this.normalizeStateCode(data.region_code),
        state: this.resolveStateName(data.region_code, data.region),
        zip: data.postal || '',
        source: 'ip'
      })),
      () => fetch('https://ipwho.is/').then((response) => {
        if (!response.ok) throw new Error('ipwho failed');
        return response.json();
      }).then((data) => {
        if (!data.success) throw new Error('ipwho failed');
        return {
          city: data.city || '',
          stateCode: this.normalizeStateCode(data.region_code),
          state: this.resolveStateName(data.region_code, data.region),
          zip: data.postal || '',
          source: 'ip'
        };
      })
    ];

    for (const provider of providers) {
      try {
        const location = await provider();
        if (location.state || location.stateCode) {
          this.storeAndApply(location);
          return;
        }
      } catch (error) {
        continue;
      }
    }

    this.storeAndApply({
      city: '',
      stateCode: '',
      state: 'your state',
      zip: '',
      source: 'fallback'
    });
  }

  async submitZip() {
    if (!this.zipInput || !this.submitButton) return;

    const zip = this.zipInput.value.replace(/\D/g, '').slice(0, 5);
    this.zipInput.value = zip;
    this.clearError();

    if (zip.length !== 5) {
      this.showError(this.zipInput.dataset.invalidMessage || 'Please enter a valid 5-digit ZIP code.');
      return;
    }

    this.submitButton.disabled = true;

    try {
      const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (!response.ok) throw new Error('zip lookup failed');

      const data = await response.json();
      const place = data.places && data.places[0] ? data.places[0] : null;
      if (!place) throw new Error('zip lookup empty');

      const stateCode = this.normalizeStateCode(place['state abbreviation']);
      const location = {
        city: place['place name'] || '',
        stateCode,
        state: this.resolveStateName(stateCode, place.state),
        zip,
        source: 'zip'
      };

      this.storeAndApply(location);
      this.closeModal();
    } catch (error) {
      this.showError(this.zipInput.dataset.invalidMessage || 'Please enter a valid 5-digit ZIP code.');
    } finally {
      this.submitButton.disabled = false;
    }
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
  }

  openModal(trigger) {
    this.activeTrigger = trigger;
    this.clearError();

    if (this.location && this.location.zip && this.zipInput) {
      this.zipInput.value = this.location.zip;
    } else if (this.zipInput) {
      this.zipInput.value = '';
    }

    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    this.positionPanel(trigger);

    if (this.desktopQuery.matches) {
      document.body.style.overflow = '';
    } else {
      document.body.style.overflow = 'hidden';
    }

    if (this.zipInput) {
      this.zipInput.focus();
    }
  }

  closeModal() {
    const trigger = this.activeTrigger;

    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    this.activeTrigger = null;
    this.modal.querySelector('.hvac-048-location-modal__panel')?.removeAttribute('style');
    document.body.style.overflow = '';

    if (trigger) {
      trigger.focus();
    }
  }

  positionPanel(trigger) {
    const panel = this.modal.querySelector('.hvac-048-location-modal__panel');
    if (!panel || !trigger) return;

    if (this.desktopQuery.matches) {
      const rect = trigger.getBoundingClientRect();
      const panelWidth = panel.offsetWidth || 315;
      const left = Math.min(Math.max(rect.right - panelWidth, 16), window.innerWidth - panelWidth - 16);
      const top = rect.bottom + 8;

      panel.style.left = `${left}px`;
      panel.style.top = `${top}px`;
      panel.style.transform = 'none';
    } else {
      panel.removeAttribute('style');
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new Hvac048LocalizedMessaging();
  });
} else {
  new Hvac048LocalizedMessaging();
}
